import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Cog, Image as ImageIcon, Trash2, Upload } from 'lucide-react'
import { SectionCard } from '../../../components/patterns/SectionCard'
import { serverApi, uploadApi } from '../../../services/api'
import { useServerStore } from '@/shell/stores/serverStore'
import { useAuthStore } from '../../../stores/authStore'
import { dialog } from '../../../services/dialog'
import { mediaUrl } from '../../../services/config'
import { canManageServer } from './canManageServer'
import type { Server } from '../../../types'

export function ServerSettingsTab({ serverId }: { serverId: string }) {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const { updateServer, deleteServer } = useServerStore()

  const [server, setServer] = useState<Server | null>(null)
  const [name, setName] = useState('')
  const [iconUrl, setIconUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    serverApi
      .get(serverId)
      .then((res) => {
        if (!live) return
        setServer(res.data)
        setName(res.data.name ?? '')
        setIconUrl(res.data.iconUrl ?? null)
      })
      .catch(() => live && setError('Failed to load server'))
      .finally(() => live && setLoading(false))
    return () => {
      live = false
    }
  }, [serverId])

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>
  if (!server) return <div className="p-6 text-sm text-destructive">{error ?? 'Server not found'}</div>
  if (!canManageServer(server, user)) {
    return <div className="p-6 text-sm text-muted-foreground">You don't have permission to manage this server.</div>
  }

  const saveName = async () => {
    const trimmed = name.trim()
    if (!trimmed || trimmed === server.name) return
    setBusy(true)
    setError(null)
    try {
      const updated = await updateServer(serverId, { name: trimmed })
      setServer(updated)
    } catch {
      setError('Failed to save name')
    } finally {
      setBusy(false)
    }
  }

  const onPickIcon = async (file: File) => {
    setBusy(true)
    setError(null)
    try {
      const up = await uploadApi.upload([file])
      const url = up.data.files[0]?.fileUrl
      if (!url) throw new Error('no url')
      const updated = await updateServer(serverId, { iconUrl: url })
      setServer(updated)
      setIconUrl(updated.iconUrl ?? null)
    } catch {
      setError('Failed to upload icon')
    } finally {
      setBusy(false)
    }
  }

  const onDelete = async () => {
    const typed = await dialog.prompt({
      message: `This permanently deletes "${server.name}". Type the server name to confirm.`,
      placeholder: server.name ?? '',
      tone: 'destructive',
      confirmLabel: 'Delete server',
    })
    if (typed == null) return // cancelled
    if (typed.trim() !== (server.name ?? '').trim()) {
      setError('The name you typed does not match — server not deleted.')
      return
    }
    setBusy(true)
    try {
      await deleteServer(serverId)
      navigate('/channels/@me')
    } catch {
      setError('Failed to delete server')
      setBusy(false)
    }
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="p-6 space-y-6 max-w-2xl">
        {error && <div className="text-sm text-destructive">{error}</div>}

        <SectionCard icon={<Cog size={14} />} title="Server name">
          <div className="flex items-end gap-3">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              className="flex-1 rounded-md bg-muted px-3 py-2 text-sm text-foreground outline-none ring-1 ring-border focus:ring-2 focus:ring-primary"
            />
            <button
              onClick={saveName}
              disabled={busy || !name.trim() || name.trim() === server.name}
              className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </SectionCard>

        <SectionCard icon={<ImageIcon size={14} />} title="Server icon">
          <div className="flex items-center gap-4">
            <span className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl bg-muted text-lg font-semibold text-muted-foreground">
              {iconUrl ? (
                <img src={mediaUrl(iconUrl)} alt="" className="h-full w-full object-cover" />
              ) : (
                (server.name?.[0] ?? '?').toUpperCase()
              )}
            </span>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm text-foreground ring-1 ring-border hover:bg-muted/70">
              <Upload size={14} />
              {iconUrl ? 'Replace icon' : 'Upload icon'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                className="hidden"
                disabled={busy}
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) onPickIcon(f)
                  e.target.value = ''
                }}
              />
            </label>
          </div>
        </SectionCard>

        <SectionCard icon={<Trash2 size={14} />} title="Danger zone" subtitle="Irreversible">
          <button
            onClick={onDelete}
            disabled={busy}
            className="rounded-md bg-destructive/15 px-3 py-2 text-sm font-medium text-destructive ring-1 ring-destructive/30 hover:bg-destructive/25 disabled:opacity-50"
          >
            Delete server
          </button>
        </SectionCard>
      </div>
    </div>
  )
}
